import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutlineRounded";
import RotateLeftIcon from "@mui/icons-material/RotateLeftRounded";
import { Button, Chip, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { ConditionConfiguration } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { selectFieldCaptionLookup } from "../../../../store/selectors/metaDataSelectors.ts";
import { resolveColumnLabel } from "../../../viewer/financialStatement/statementGridModel.ts";
import { sanitizeFieldName } from "../../common/utilities/sanitizeFieldName.ts";
import {
  getLabelPreview,
  getLabelTokenNames,
  getUnknownTokensMessage,
  insertLabelToken,
} from "../utils/statementColumns.ts";

interface Props {
  customLabel: string | undefined;
  defaultLabel: string;
  conditions: ConditionConfiguration[];
  readonly: boolean;
  onChange: (customLabel: string) => void;
}

const DataColumnLabelField = ({ customLabel, defaultLabel, conditions, readonly, onChange }: Props) => {
  const getFieldCaption = useSelector(selectFieldCaptionLookup);
  const [draft, setDraft] = useState<string>();
  const inputRef = useRef<HTMLInputElement>(null);

  const label = customLabel?.trim() || defaultLabel;
  const resolved = resolveColumnLabel(label, conditions, getFieldCaption);
  const unknownTokensMessage = getUnknownTokensMessage(label, conditions, getFieldCaption);
  const hasTokens = resolved.text !== label || resolved.unknownTokens.length > 0;

  const handleApply = () => {
    if (draft?.trim()) {
      onChange(draft);
      setDraft(undefined);
    }
  };

  const handleInsertToken = (tokenName: string) => {
    const text = draft ?? "";
    const input = inputRef.current;
    const start = input?.selectionStart ?? text.length;
    const insertion = insertLabelToken(text, start, input?.selectionEnd ?? start, tokenName);
    setDraft(insertion.text);
    requestAnimationFrame(() => input?.setSelectionRange(insertion.cursor, insertion.cursor));
  };

  if (draft !== undefined) {
    const tokenNames = getLabelTokenNames(conditions, getFieldCaption);
    return (
      <Stack sx={{ gap: 0.75, px: 2, pt: 2, pb: 1 }}>
        <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
          <TextField
            autoFocus
            inputRef={inputRef}
            value={draft}
            placeholder={defaultLabel}
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                endAdornment: (
                  <Tooltip title="Reset to Default Label" placement="bottom" arrow disableInteractive>
                    <RotateLeftIcon
                      onClick={() => setDraft(defaultLabel)}
                      sx={(theme) => ({ cursor: "pointer", color: theme.palette.secondary.light })}
                    />
                  </Tooltip>
                ),
              },
            }}
            onChange={(event) => setDraft(sanitizeFieldName(event.target.value))}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleApply();
              }
            }}
          />
          <Button variant="outlined" color="secondary" onClick={() => setDraft(undefined)}>
            Cancel
          </Button>
          <Button variant="contained" disabled={!draft.trim()} onClick={handleApply}>
            Apply
          </Button>
        </Stack>
        {tokenNames.length > 0 && (
          <Stack direction="row" sx={{ flexWrap: "wrap", alignItems: "center", gap: 0.5 }}>
            <Typography variant="caption" color="textSecondary">
              Insert condition:
            </Typography>
            {tokenNames.map((tokenName) => (
              <Chip
                key={tokenName}
                label={`@${tokenName}`}
                color="primary"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleInsertToken(tokenName)}
              />
            ))}
          </Stack>
        )}
        <Typography variant="caption" color="textSecondary">
          {getLabelPreview(draft, conditions, getFieldCaption, defaultLabel)}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: 0.5, px: 2, pt: 2, pb: 1 }}>
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" sx={{ alignItems: "center", gap: 0.75 }}>
          <Typography sx={{ width: 36, flex: "none" }}>Label:</Typography>
          <Typography variant="subtitle1" component="span" noWrap>
            {resolved.text}
          </Typography>
        </Stack>
        {hasTokens && (
          <Typography variant="caption" color="textSecondary" noWrap>
            Dynamic Label - {label}
          </Typography>
        )}
        {unknownTokensMessage && (
          <Typography variant="caption" color="error">
            {unknownTokensMessage}
          </Typography>
        )}
        {label !== defaultLabel && (
          <Typography variant="caption" noWrap>
            Default Label - {defaultLabel}
          </Typography>
        )}
      </Stack>
      {!readonly && (
        <Tooltip title="Rename" placement="bottom" arrow disableInteractive>
          <IconButton onClick={() => setDraft(label)}>
            <DriveFileRenameOutlineIcon sx={(theme) => ({ color: theme.palette.secondary.light })} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

export default DataColumnLabelField;
